import * as React from 'react';

import { cn } from '@/lib/utils';
import Button from '../button/button.component';

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('rounded shadow border border-other-stroke flex-1', className)} {...props} />
));
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-3 p-4', className)} {...props}>
      {props.children}
    </div>
  ),
);
CardHeader.displayName = 'CardHeader';

interface CardHeaderTitleProps extends React.HTMLAttributes<HTMLButtonElement> {
  valueTestId?: string;
}

const CardHeaderTitle = React.forwardRef<HTMLButtonElement, CardHeaderTitleProps>(({ className, ...props }, ref) => (
  <Button ref={ref} className={cn('truncate', className)} {...props} variant="ghost">
    <p title={props.title} data-testid={props.valueTestId} className="typo-primary-500 truncate">
      {props.children}
    </p>
  </Button>
));
CardHeaderTitle.displayName = 'CardHeaderTitle';

const CardHeaderDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('typo-secondary truncate text-secondary-text', className)} {...props}>
      {props.children}
    </p>
  ),
);
CardHeaderDescription.displayName = 'CardHeaderDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('px-4 py-3', className)} {...props}>
        {props.children}
      </div>
    );
  },
);
CardContent.displayName = 'CardContent';

const CardDivider = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('h-[1px] border-b border-other-stroke', className)} {...props} />
  ),
);
CardDivider.displayName = 'CardDivider';

export interface CardDataRowProps extends React.HTMLAttributes<HTMLDivElement> {
  context: string;
  id: string;
  name: string;
  label: React.ReactNode;
  value: React.ReactNode;
}

const CardDataRow = React.forwardRef<HTMLDivElement, CardDataRowProps>(
  ({ className, label, value, id, context, name, ...props }, ref) => (
    <div ref={ref} {...props} className={cn('flex flex-row justify-between items-center', className)}>
      <p data-testid={`${context}-card-${id}-${name}-text`} className="typo-secondary text-secondary-text mr-2">
        {label}
      </p>
      <div data-testid={`${context}-card-${id}-${name}-value`} className="typo-secondary truncate">
        {value}
      </div>
    </div>
  ),
);
CardDataRow.displayName = 'CardDataRow';

export const CardUpdatedAt = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('typo-caption text-secondary-text', className)} {...props} />
  ),
);
CardUpdatedAt.displayName = 'CardUpdatedAt';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('typo-primary-500 mb-3 truncate', className)} {...props} />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('typo-secondary text-secondary-text truncate', className)} {...props} />
  ),
);
CardDescription.displayName = 'CardDescription';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('', className)} {...props} />,
);
CardFooter.displayName = 'CardFooter';

const CardFooterItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-row justify-between items-center mb-3', className)} {...props} />
  ),
);
CardFooterItem.displayName = 'CardFooterItem';

export {
  Card,
  CardContent,
  CardDataRow,
  CardDescription,
  CardDivider,
  CardFooter,
  CardFooterItem,
  CardHeader,
  CardHeaderDescription,
  CardHeaderTitle,
  CardTitle,
};
